import {
  doc,
  onSnapshot,
  where,
  collection,
  limit,
  query,
} from 'firebase/firestore'
import { db } from './firebase.js'
import { callHostedTool } from './actor-service.js'
import { t } from './portal-utils.js'
import {
  renderList,
  renderApprovalItem,
  renderBlockedPortal,
  renderMigaState,
  renderMigaMeta,
} from './portal-renderers.js'
import { submitPortalApprovalDecision } from './portal-services.js'
import { setSafeHtml } from './safe-html.js'
import {
  isSeededFixtureWorkspace,
  readSeededQueryFromStorage,
} from './portal-seeded-fixtures.js'
import {
  updateSnapshotUI,
  updateSnapshotUIDegraded,
  updateMigaFromSnapshot,
} from './portal-ui-updaters.js'

/**
 * Create real-time Firestore subscriptions for a workspace.
 * Returns an object with an unsubscribe() method to tear down all listeners.
 */
export function createWorkspaceSubscriptions(root: HTMLElement, { workspaceSelect, migaOutput, migaMeta }: { workspaceSelect: HTMLSelectElement, migaOutput: Element, migaMeta: HTMLElement }) {
  const unsubscribers: Array<() => void> = []

  function teardownSubscriptions() {
    while (unsubscribers.length > 0) {
      const unsubscribe = unsubscribers.pop()
      try {
        unsubscribe?.()
      } catch {
        // ignore
      }
    }
  }

  function subscribeToWorkspaceSnapshot(workspaceId: string) {
    const snapshotRef = doc(db, 'workspaces', workspaceId, 'snapshot', 'current')
    unsubscribers.push(
      onSnapshot(
        snapshotRef,
        (snapshotDoc) => {
          const snapshot = snapshotDoc.exists() ? snapshotDoc.data() : {}
          updateSnapshotUI(root, snapshot)
          updateMigaFromSnapshot(root, snapshot, workspaceSelect, migaOutput, migaMeta)
        },
        (error) => {
          // VAL-WEB-016: Surface degraded live-state messaging on listener failures
          console.error('Workspace snapshot listener failed:', error)
          updateSnapshotUIDegraded(root, 'Live workspace data is temporarily unavailable. Last known state shown.')
        }
      )
    )
  }

  function subscribeToApprovals(workspaceId: string) {
    const approvalsQuery = query(collection(db, 'workspaces', workspaceId, 'approvals'), where('status', '==', 'pending'), limit(20))
    unsubscribers.push(
      onSnapshot(
        approvalsQuery,
        (approvalSnap) => {
          const approvals = approvalSnap.docs
            .map((row) => /** @type {any} */ ({ id: row.id, ...row.data() }))
            .sort((left: any, right: any) => t(right.created_at).localeCompare(t(left.created_at)))
          const approvalRoot = root.querySelector('#op-approval-inbox')
          setSafeHtml(approvalRoot, renderList(approvals, renderApprovalItem, 'No pending approvals in Firestore right now.', { tone: 'success', headline: 'Inbox is clear.' }))
          bindApprovalActions(approvalRoot, workspaceId)
        },
        (error) => {
          // VAL-WEB-016: Surface degraded live-state messaging on listener failures
          console.error('Approvals listener failed:', error)
          const approvalRoot = root.querySelector('#op-approval-inbox')
          if (approvalRoot) {
            setSafeHtml(approvalRoot, '<div class="op-alert">Live approval data is temporarily unavailable. Last known state shown.</div>')
          }
        }
      )
    )
  }

  function bindApprovalActions(approvalRoot: Element | null, workspaceId: string) {
    if (!approvalRoot) return
    approvalRoot.querySelectorAll('[data-approval-action]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        const target = event.currentTarget as Element | null
        const action = target?.getAttribute('data-approval-action')
        const approvalId = target?.closest('[data-approval-id]')?.getAttribute('data-approval-id')
        if (!approvalId || !action) return
        button.setAttribute('disabled', 'true')
        // VAL-WEB-017: Clear prior per-item error before new attempt
        const itemEl = target?.closest('[data-approval-id]')
        const existingAlert = itemEl?.querySelector('.op-item-error')
        if (existingAlert) existingAlert.remove()
        try {
          await submitPortalApprovalDecision(workspaceId, approvalId, action)
        } catch (err) {
          // VAL-WEB-017: Show per-item visible alert on failure, keep controls retryable
          if (itemEl) {
            const alertDiv = document.createElement('div')
            alertDiv.className = 'op-item-error op-alert'
            alertDiv.textContent = err instanceof Error ? err.message : String(err)
            itemEl.appendChild(alertDiv)
          }
        } finally {
          button.removeAttribute('disabled')
        }
      })
    })
  }

  // VAL-WEB-014 & VAL-WEB-021: Probe hosted config for active workspace with race safety
  let latestWorkspaceSelection: string | null = null
  let hostedProbeSequence = 0

  async function probeHostedConfig(workspaceId: string) {
    if (isSeededFixtureWorkspace(workspaceId)) {
      return
    }
    const probeSeq = ++hostedProbeSequence
    try {
      const config = await callHostedTool('getHostedConfigStatus', { workspaceId })
      // VAL-WEB-021: Ignore stale probe responses
      if (probeSeq !== hostedProbeSequence || latestWorkspaceSelection !== workspaceId) {
        return
      }
      if (!config.canRead) {
        setSafeHtml(root, renderBlockedPortal(config))
      }
    } catch {
      // VAL-WEB-021: Ignore stale probe failures
      if (probeSeq !== hostedProbeSequence || latestWorkspaceSelection !== workspaceId) {
        return
      }
      setSafeHtml(root, renderBlockedPortal({
        status: 'blocked',
        missing: ['hosted_control_plane'],
        guidance: ['The hosted operator function is not reachable or is misconfigured.'],
        canRead: false,
        canWrite: false
      }))
    }
  }

  function bindWorkspace(workspaceId: string) {
    teardownSubscriptions()
    if (!workspaceId) {
      return
    }
    latestWorkspaceSelection = workspaceId
    probeHostedConfig(workspaceId)
    subscribeToWorkspaceSnapshot(workspaceId)
    subscribeToApprovals(workspaceId)
    if (isSeededFixtureWorkspace(workspaceId)) {
      const seededQuery = readSeededQueryFromStorage(workspaceId)
      if (seededQuery?.content) {
        setSafeHtml(migaOutput, renderMigaState(seededQuery))
        migaMeta.textContent = renderMigaMeta(seededQuery)
      }
    }
  }

  return {
    unsubscribe: teardownSubscriptions,
    bindWorkspace,
  }
}
